import { useEffect, useState } from "react";
import { useSignal } from "@preact/signals-react";
import { FormattedMessage as FM } from "react-intl";

import proxy from "api/proxy";
import { getStorage } from "store/local";
import { UserIcon, IdentificationIcon } from "components/Icons";
import PasswordField from "componentsv2/form/Password";
import Button from "componentsv2/button/Button";
import {
  MessageContainer,
  useMessage,
} from "componentsv2/experimental/Message";

const Profile = () => {
  const session = getStorage("ctxSession");
  const [party_, setParty] = useState(null);
  const { user_name, user, shop_ } = session;
  const newPassword = useSignal("");
  const confirmPassword = useSignal("");
  const msg = useMessage();
  const activeTab = useSignal("info");

  async function handleChangePassword() {
    if (newPassword.value.length < 8) {
      msg.error("profile.password_min_length");
      return;
    }
    if (newPassword.value !== confirmPassword.value) {
      msg.error("profile.password_mismatch");
      return;
    }
    const { error } = await proxy.save({
      model: "res.user",
      values: { password: newPassword.value },
      ids: [user],
    });
    if (error) {
      msg.error(error?.message || "profile.password_error");
    } else {
      msg.success("profile.password_changed");
      newPassword.value = "";
      confirmPassword.value = "";
    }
  }

  const getPartyInformation = async () => {
    const fields = [
      "employee",
      "employees",
      "employee.party.name",
      "employee.party.id_number",
      "employee.party.phone",
      "employee.party.street",
      "employee.party.city",
      "employee.party.birthday",
      "employee.party.main_email",
      "employee.party.main_mobile",
    ];

    const { data } = await proxy.search(
      "res.user",
      [["id", "=", user]],
      fields,
      1,
    );

    if (!data?.length) return;

    const entry = data[0];
    const party = entry?.["employee."]?.["party."];

    if (party) {
      setParty(party);
    }
  };

  useEffect(() => {
    getPartyInformation();
  }, [user]);

  const tabClass = (tab) =>
    `p-3 rounded cursor-pointer transition-colors ${
      activeTab.value === tab
        ? "bg-neutral-200 font-semibold text-neutral-900"
        : "text-neutral-600 hover:bg-neutral-100"
    }`;

  const InfoRow = ({ label, value }) => (
    <li className="py-1.5 text-md">
      <span className="font-semibold text-slate-600">
        <FM id={label} />:
      </span>{" "}
      <span className="text-slate-600">{value || "—"}</span>
    </li>
  );

  return (
    <div id="profile" className="w-full shadow-lg rounded-lg">
      <section className="h-32 -mt-2 bg-neutral-800 mb-5 relative">
        <div className="relative inset-y-14 px-20">
          <img
            className="w-24 h-24 object-cover rounded-full border-2"
            src={`https://ui-avatars.com/api/?name=${user_name}&background=0D8ABC&color=fff `}
            alt="User Profile"
          />
        </div>
        <div className="relative left-48 -top-10 space-y-1">
          <h2 className="text-2xl font-bold text-white">
            <span className="flex items-center space-x-2">
              <span>
                <UserIcon className="size-5  fill-current" />
              </span>
              <span>{party_?.name}</span>
            </span>
          </h2>
          <h2 className="text-md text-white">
            <span className="flex items-center space-x-2">
              <IdentificationIcon className="w-5 h-5 fill-current" />
              <span>
                {party_?.id_number?.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
              </span>
            </span>
          </h2>
        </div>
      </section>
      <div className="flex px-20 mt-20">
        <aside className="w-1/4 pr-8 border-r border-neutral-200">
          <nav className="space-y-1">
            <div
              onClick={() => (activeTab.value = "info")}
              className={tabClass("info")}
            >
              <FM id="profile.general_information" />
            </div>
            <div
              onClick={() => (activeTab.value = "password")}
              className={tabClass("password")}
            >
              <FM id="profile.change_password" />
            </div>
          </nav>
        </aside>
        <main className="w-3/4 pl-10">
          {activeTab.value === "info" && (
            <div>
              <p className="text-xl text-gray-600 font-semibold mb-3">
                <FM id="personal_information" />
              </p>
              <ul className="space-y-1">
                <InfoRow
                  label="party.party.id_number"
                  value={party_?.id_number}
                />
                <InfoRow
                  label="party.party.birthday"
                  value={party_?.birthday}
                />
                <InfoRow
                  label="party.party.main_mobile"
                  value={party_?.main_mobile}
                />
                <InfoRow
                  label="party.party.main_email"
                  value={party_?.main_email}
                />
                <li className="py-1.5">
                  <span className="font-semibold text-slate-700">
                    <FM id="party.party.address" />:
                  </span>{" "}
                  <span className="text-slate-600">
                    {[party_?.city, party_?.street]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </span>
                </li>
              </ul>
              {shop_?.name && (
                <>
                  <p className="text-lg text-gray-600 font-semibold mt-8 mb-3">
                    <FM id="profile.terminal" />
                  </p>
                  <ul className="space-y-1">
                    <li className="py-1.5">
                      <span className="font-semibold text-slate-700">
                        <FM id="profile.terminal_name" />:
                      </span>{" "}
                      <span className="text-slate-600">{shop_.name}</span>
                    </li>
                  </ul>
                </>
              )}
            </div>
          )}
          {activeTab.value === "password" && (
            <div className="max-w-md">
              <h2 className="text-xl font-bold mb-6">
                <FM id="profile.change_password" />
              </h2>
              <form className="grid gap-y-3">
                <PasswordField
                  name="newPassword"
                  label="profile.new_password"
                  data={newPassword}
                />
                <PasswordField
                  name="confirmPassword"
                  label="profile.confirm_password"
                  data={confirmPassword}
                />
                <div className="mt-4">
                  <Button
                    onClick={handleChangePassword}
                    content="button.save"
                    color="black"
                  />
                </div>
              </form>
            </div>
          )}
        </main>
      </div>
      <MessageContainer
        messages={msg.messages}
        onMessageClose={msg.hide}
        position="bottom-center"
      />
    </div>
  );
};

export default Profile;
